import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Container, Divider, List, ListItem, ListItemText, Typography, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { db } from '../../firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';

// Placeholder Firebase configuration similar to provided firebase.js
const firebaseConfig = {
  apiKey: "AIzaSyXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX",
  authDomain: "your-project-id.firebaseapp.com",
  projectId: "your-project-id",
  storageBucket: "your-project-id.firebasestorage.app",
  messagingSenderId: "123456789012",
  appId: "1:123456789012:web:xxxxxxxxxxxxxxxxxxxxxx",
  measurementId: "G-XXXXXXXXXX"
};

export default function TrackOrder({ unitCode }) {
  const location = useLocation();
  const state = location?.state;
  const navigate = useNavigate();
  const [orderStatus, setOrderStatus] = useState(state?.status || localStorage.getItem('currentOrderStatus') || 'Confirmed');
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState(null);

  const storedOrderId = state?.orderId || localStorage.getItem('currentOrderId') || null;
  const tableId = state?.tableId || (location?.search ? new URLSearchParams(location.search).get('id') : null) || null;
  const companyCode = state?.companyCode || (location?.search ? new URLSearchParams(location.search).get('code') : null) || null;
  const stateUnitCode = state?.unitCode;
  const effectiveUnitCode = unitCode || stateUnitCode;

  const cart = state?.cart || [];
  const totalAmount = state?.totalAmount || 0;

  useEffect(() => {
    console.log('TrackOrder: Initial state:', {
      state,
      location: location?.pathname,
      query: location?.search,
      storedOrderId,
      companyCode,
      unitCode: effectiveUnitCode,
      tableId,
      cart: cart.map(item => ({ id: item.id, name: item.name, quantity: item.quantity, totalPrice: item.totalPrice })),
      totalAmount,
      orderStatus
    });

    const fetchCompanyDetails = async () => {
      if (!companyCode) {
        setCompanyName('Unknown Company');
        console.log('TrackOrder: No companyCode provided');
        return;
      }

      try {
        const companyDocRef = doc(db, 'companies', companyCode);
        const companyDoc = await getDoc(companyDocRef);
        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          setCompanyName(companyData.name || 'Unknown Company');
          console.log('TrackOrder: Company details fetched:', { name: companyData.name });
        } else {
          setCompanyName('Unknown Company');
          console.log('TrackOrder: No company found for companyCode:', companyCode);
        }
      } catch (err) {
        console.error('TrackOrder: Error fetching company details:', err);
        setCompanyName('Unknown Company');
      }
    };

    fetchCompanyDetails();

    if (!storedOrderId || !companyCode || !tableId || !effectiveUnitCode) {
      setError('Missing required parameters: ' + 
        (!storedOrderId ? 'Order ID' : '') + 
        (!companyCode ? ' Company Code' : '') + 
        (!tableId ? ' Table ID' : '') +
        (!effectiveUnitCode ? ' Unit Code' : ''));
      setLoading(false);
      return;
    }

    if (state?.orderId && state.orderId) {
      localStorage.setItem('currentOrderId', state.orderId);
      console.log('TrackOrder: Stored orderId in localStorage:', state.orderId);
    }
    if (state?.status) {
      localStorage.setItem('currentOrderStatus', state.status);
      console.log('TrackOrder: Stored status in localStorage:', state.status);
    }

    const orderRef = doc(db, 'orders', storedOrderId);
    const unsubscribe = onSnapshot(
      orderRef,
      (doc) => {
        console.log('TrackOrder: Firestore snapshot received, exists:', doc.exists());
        if (doc.exists()) {
          const data = doc.data();
          console.log('TrackOrder: Firestore data:', {
            orderId: storedOrderId,
            companyCode: data.companyCode,
            unitCode: data.unitCode,
            tableId: data.tableId,
            items: data.items,
            status: data.status,
            totalAmount: data.totalAmount,
            paid: data.paid
          });
          // Validate that the order belongs to the correct company, unit, and table
          if (data.companyCode === companyCode && data.unitCode === effectiveUnitCode && data.tableId === tableId) {
            setOrderDetails(data);
            setOrderStatus(data.status || 'Confirmed');
            localStorage.setItem('currentOrderStatus', data.status || 'Confirmed');
            setError(null);
          } else {
            setError('Order does not match the specified company, unit, or table.');
          }
        } else {
          setError('Order not found for the specified ID.');
        }
        setLoading(false);
      },
      (err) => {
        console.error('TrackOrder: Firestore error:', err);
        setError('Failed to fetch order status: ' + err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [storedOrderId, companyCode, effectiveUnitCode, tableId]);

  const handleBackToMenu = () => {
    console.log('TrackOrder: Navigating to menu:', { companyCode, unitCode: effectiveUnitCode, tableId });
    navigate(`/customer/menu?code=${companyCode}&unit=${effectiveUnitCode}&id=${tableId}`, { state: { tableId, companyCode, unitCode: effectiveUnitCode } });
  };

  const handleOrderMore = () => {
    console.log('TrackOrder: Order More clicked, paid:', orderDetails?.paid);
    if (orderDetails?.paid) {
      console.log('TrackOrder: Clearing localStorage as order is paid');
      localStorage.removeItem('currentOrderId');
      localStorage.removeItem('currentOrderStatus');
    }
    navigate(`/customer/menu?code=${companyCode}&unit=${effectiveUnitCode}&id=${tableId}`, { state: { tableId, companyCode, unitCode: effectiveUnitCode } });
  };

  const handleRequestBill = () => {
    console.log('TrackOrder: Navigating to bill:', { orderId: storedOrderId, companyCode, unitCode: effectiveUnitCode, tableId });
    navigate(`/customer/bill?code=${companyCode}&unit=${effectiveUnitCode}&id=${tableId}`, {
      state: {
        orderId: storedOrderId,
        cart: orderDetails?.items || cart,
        totalAmount: orderDetails?.totalAmount || totalAmount,
        tableId,
        companyCode,
        unitCode: effectiveUnitCode
      }
    });
  };

  const handleRetry = () => {
    console.log('TrackOrder: Retrying Firestore fetch');
    setLoading(true);
    setError(null);
    setOrderDetails(null);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'Confirmed':
        return 'warning.main';
      case 'in progress':
      case 'prepared':
        return 'info.main';
      case 'completed':
      case 'delivered':
        return 'success.main';
      case 'cancelled':
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Track Your Order - {companyName || ''}
      </Typography>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading order details...
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRetry}
            >
              Retry
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleBackToMenu}
            >
              Back to Menu
            </Button>
          </Box>
        </Box>
      ) : orderStatus.toLowerCase() === 'delivered' && orderDetails?.paid ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <CheckCircleIcon sx={{ fontSize: '4rem', color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Your order has been delivered and paid!
          </Typography>
          <Typography variant="body1" gutterBottom>
            Order ID: {storedOrderId}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Thank you for your order. Would you like to order more?
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOrderMore}
          >
            Order More
          </Button>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6">
              Order ID: {storedOrderId || 'N/A'}
            </Typography>
            <Typography variant="h6">
              Table ID: {tableId || 'N/A'}
            </Typography>
            <Typography variant="h6" sx={{ color: getStatusColor(orderStatus) }}>
              Order Status: {orderStatus}
            </Typography>
            <Typography variant="h6">
              Payment Status: {orderDetails?.paid ? 'Paid' : 'Not Paid'}
            </Typography>
            {orderDetails?.createdAt && (
              <Typography variant="body2" color="text.secondary">
                Order placed: {new Date(orderDetails.createdAt.toDate()).toLocaleString()}
              </Typography>
            )}
          </Box>

          <Typography variant="h6" gutterBottom>
            Order Details
          </Typography>
          <Divider sx={{ my: 2 }} />
          <List>
            {(orderDetails?.items || cart).map((item) => (
              <ListItem key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <ListItemText
                  primary={item.name}
                  secondary={
                    <>
                      <Typography component="span" variant="body2">
                        Quantity: {item.quantity}
                      </Typography>
                      <br />
                      <Typography component="span" variant="body2" sx={{ color: getStatusColor(item.status) }}>
                        Status: {item.status || 'Confirmed'}
                      </Typography>
                    </>
                  }
                  primaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                  secondaryTypographyProps={{ variant: 'body2' }}
                />
                <Typography variant="body1">
                  ₹{(item.totalPrice || (item.quantity * item.price)).toFixed(2)}
                </Typography>
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Total Amount:
            </Typography>
            <Typography variant="h6" color="primary">
              ₹{(orderDetails?.totalAmount || totalAmount).toFixed(2)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleBackToMenu}
            >
              Back to Menu
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRequestBill}
            >
              Request Bill
            </Button>
            {orderStatus.toLowerCase() === 'delivered' && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleOrderMore}
              >
                Order More
              </Button>
            )}
          </Box>
        </>
      )}
    </Container>
  );
}